import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

// ─── Tipos ───────────────────────────────────────────────────────────────────

/** Permissão de controller recebida no login */
export interface ControllerPermission {
  readonly controllerId: number;
  readonly controllerName: string;
}

/** Dados do usuário autenticado na sessão */
export interface SessionData {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly roles: string[];
  /** Token JWT de acesso */
  readonly accessToken: string;
  readonly tokenExpireDate: Date;
  /** ID da pessoa (personId da API) — usado em operações de autoria */
  readonly personId: number;
  /** Controllers que o usuário tem permissão de acessar */
  readonly controllerPermissions: ControllerPermission[];
}

// ─── Átomos primitivos ────────────────────────────────────────────────────────

/**
 * Átomo da sessão do usuário autenticado.
 *
 * `null` indica que o usuário não está autenticado.
 *
 * NUNCA use diretamente em componentes — use `useSession()`.
 */
export const sessionAtom = atomWithStorage<SessionData | null>('session', null, undefined, {
  getOnInit: true,
});
sessionAtom.debugLabel = 'shared/sessionAtom';

// ─── Átomos derivados ────────────────────────────────────────────────────────

/**
 * Átomo derivado que indica se o usuário está autenticado.
 *
 * Lógica computada no átomo — não compute nos componentes.
 */
export const isAuthenticatedDerivedAtom = atom(get => get(sessionAtom) !== null);
isAuthenticatedDerivedAtom.debugLabel = 'shared/isAuthenticatedDerivedAtom';

/**
 * Átomo derivado com a role do usuário autenticado.
 * Retorna `null` se não autenticado.
 */
export const userRoleDerivedAtom = atom(get => get(sessionAtom)?.roles ?? null);
userRoleDerivedAtom.debugLabel = 'shared/userRoleDerivedAtom';

/**
 * Átomo derivado com as permissões de controller do usuário autenticado.
 * Retorna array vazio se não autenticado.
 */
export const controllerPermissionsDerivedAtom = atom(
  get => get(sessionAtom)?.controllerPermissions ?? [],
);
controllerPermissionsDerivedAtom.debugLabel = 'shared/controllerPermissionsDerivedAtom';
